import { CatalogProductShared, ProductShared } from '@/modules/products/shared/types';
import { BaseApiService } from '@/services/base-service';
import { DeliveryType } from '@/modules/organizations/types';
import {
  ApiPagination, BaseResponse, FiltersPagination, Response, Filters,
} from '@/types';
import { prepare_formdata } from '@/utils';
import { LicenseKeys } from '@/modules/products/types';
import {
  Cart,
  CartDetail,
  CartHistoryItem,
  CartOrganization,
  FiltersOrganization,
  HistroyCartsFilters,
  DelLine,
  CartFileUploadData,
  CartProject,
  UsersFilter,
} from '../types';

export const default_cart_detail = (): CartDetail => ({
  id: 0,
  external_number: null,
  external_id: '',
  created_at: '',
  discount: 0,
  products: [],
  orders: [],
  price: 0,
  is_project: false,
  is_external_api: false,
  external_api_cart_id: null,
});

interface CartHistoryResponse extends BaseResponse, ApiPagination {
  data: {
    items: CartHistoryItem[];
    organizations: FiltersOrganization;
    dates: string[];
    users: UsersFilter[]
  };
}

export class CartService extends BaseApiService {
  private readonly base_path = 'cart';

  async send_cart(
    prods: ProductShared | ProductShared[],
    warehouse_id: number | null = null,
    license_key: string | LicenseKeys[] | null,
    is_realization_contract = false,
  ): Promise<Cart.CartResponse> {
    let payload = {} as Cart.CartPayload;

    if (Array.isArray(prods)) {
      const items = prods.map((prod) => {
        const obj = {
          product_id: prod.id,
          product_code: prod.product_code,
          nomenclature: prod.nomenclature,
          quantity: prod.quantity,
          license_key: prod.is_required_license ? null : prod.license_key as string | null,
          banner_id: prod.banner_id,
          page: prod.page ? prod.page : null,
        };

        if (prod.is_required_license && !prod.is_related) {
          if (Array.isArray(license_key)) {
            const lcs = license_key.find((k) => k.id === obj.product_id);

            obj.license_key = lcs?.license_key || null;
          } else if (license_key) {
            obj.license_key = license_key;
          }
        }
        return obj;
      });

      payload = {
        warehouse_id,
        is_realization_contract,
        items,
      };
    } else {
      payload = {
        warehouse_id,
        is_realization_contract,
        items: [
          {
            product_id: prods.id,
            product_code: prods.product_code,
            nomenclature: prods.nomenclature,
            quantity: prods.quantity,
            license_key: (prods.is_required_license && typeof license_key === 'string') ? license_key : null,
            banner_id: prods.banner_id,
            page: prods.page ? prods.page : null,
          },
        ],
      };
    }

    const { data } = await this.http_client.post<Cart.CartResponse>(this.base_path, payload);

    return data;
  }

  async send_cart1(
    prods: CatalogProductShared | CatalogProductShared[],
    warehouse_id: number | null = null,
    license_key: string | LicenseKeys[] | null,
    is_realization_contract = false,
  ): Promise<Cart.CartResponse> {
    let payload = {} as Cart.CartPayload;

    if (Array.isArray(prods)) {
      const items = prods.map((prod) => {
        const obj = {
          product_id: prod.id,
          product_code: prod.product_code,
          nomenclature: prod.nomenclature,
          quantity: prod.quantity,
          license_key: prod.is_required_license ? null : prod.license_key as string | null,
          banner_id: prod.banner_id,
          page: prod.page ? prod.page : null,
        };

        if (prod.is_required_license && !prod.is_related) {
          if (Array.isArray(license_key)) {
            const lcs = license_key.find((k) => k.id === obj.product_id);

            obj.license_key = lcs?.license_key || null;
          } else if (license_key) {
            obj.license_key = license_key;
          }
        }
        return obj;
      });

      payload = {
        is_realization_contract,
        warehouse_id,
        items,
      };
    } else {
      payload = {
        is_realization_contract,
        warehouse_id,
        items: [
          {
            product_id: prods.id,
            product_code: prods.product_code,
            nomenclature: prods.nomenclature,
            quantity: prods.quantity,
            license_key: (prods.is_required_license && typeof license_key === 'string') ? license_key : null,
            banner_id: prods.banner_id,
            page: prods.page ? prods.page : null,
          },
        ],
      };
    }

    const { data } = await this.http_client.post<Cart.CartResponse>(this.base_path, payload);

    return data;
  }

  async get_cart(filters: Filters): Promise<Cart.CartResponse> {
    const payload = filters;

    const { data } = await this.http_client.get<Cart.CartResponse>(this.base_path, {
      params: {
        filters: payload.filters,
      },
    });

    return data;
  }

  async get_cart_organizations(): Promise<Response<CartOrganization[]>> {
    const { data } = await this.http_client.get<Response<CartOrganization[]>>(
      `${this.base_path}/organizations`,
    );

    return data;
  }

  async get_delivery_types(id: string | number): Promise<Response<DeliveryType[]>> {
    const url = `${this.base_path}/organizations/${id}/delivery_types`;
    const { data } = await this.http_client.get<Response<DeliveryType[]>>(url);

    return data;
  }

  async upd_cart(
    payload_base: Cart.CartUpdPayload,
  ): Promise<Cart.CartResponse> {
    const {
      warehouse_id, project_id, items, filters,
    } = payload_base;

    const payload: Cart.CartUpdPayload = {
      filters,
      warehouse_id,
      project_id,
      items: [],
    };

    if (Array.isArray(items)) {
      payload.items = items;
    } else {
      payload.items = [
        {
          id: items.id,
          quantity: items.quantity,
          payment_type: items.payment_type,
          license_key: items.license_key,
          is_selected: items.is_selected,
        },
      ];
    }

    const { data } = await this.http_client.patch<Cart.CartResponse>(this.base_path, payload);

    return data;
  }

  async get_history_carts(
    payload: FiltersPagination<HistroyCartsFilters>,
  ): Promise<CartHistoryResponse> {
    const url = `${this.base_path}/history`;
    const { data } = await this.http_client.get<CartHistoryResponse>(url, {
      params: {
        filters: payload.filters,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }

  async get_cart_detail(id: string | number): Promise<Response<CartDetail>> {
    const url = `${this.base_path}/history/${id}`;
    const { data } = await this.http_client.get<Response<CartDetail>>(url);

    return data;
  }

  async get_cart_projects(): Promise<Response<CartProject[]>> {
    const url = `${this.base_path}/projects`;
    const { data } = await this.http_client.get<Response<CartProject[]>>(url);

    return data;
  }

  async retry_cart(id: number): Promise<void> {
    const url = `${this.base_path}/history/${id}/retry`;

    await this.http_client.post<void>(url);
  }

  async delete_products(): Promise<void> {
    await this.http_client.delete(`${this.base_path}/all`);
  }

  async send_cart_file(file: File, is_realization_contract = false): Promise<Response<CartFileUploadData>> {
    const url = `${this.base_path}/import`;
    const { data } = await this.http_client.post<Response<CartFileUploadData>>(
      url,
      prepare_formdata({ file, is_realization_contract }),
    );

    return data;
  }

  async dl_places_search(search: string): Promise<Response<DelLine.DelLinePlace[]>> {
    const url = 'dellin';
    const { data } = await this.http_client.get<Response<DelLine.DelLinePlace[]>>(url, {
      params: { city: search },
    });

    return data;
  }

  async get_delline_terminals(id: string | number): Promise<Response<DelLine.DelLineTerminal[]>> {
    const url = 'dellin';
    const { data } = await this.http_client.get<Response<DelLine.DelLineTerminal[]>>(url, {
      params: { city_id: id },
    });

    return data;
  }

  async get_license_keys(id: number, search?: string): Promise<any> {
    const { data } = await this.http_client.get<Response<any>>(`products/${id}/license_keys`, {
      params: { search },
    });

    return data;
  }

  set_realization_contract(payload: { is_realization_contract: boolean }) {
    return this.http_client.post(`${this.base_path}/realization`, payload);
  }

  async set_cash_registers_auto_addition(payload: { is_cash_registers_auto_addition: boolean }) {
    await this.http_client.post(`${this.base_path}/cash_registers_auto_addition`, payload);
  }

  async get_catalog_products(payload: Cart.CatalogProductRequest): Promise<Cart.CatalogProductResponse> {
    const payload_filters = payload.filters;

    payload_filters.search = payload_filters.search ? payload_filters.search : '';

    const { data } = await this.http_client.get<Cart.CatalogProductResponse>(`${this.base_path}/catalog_products`, {
      params: {
        filters: payload_filters,
        filters_exist: payload.filters_exist,
        limit: payload.pagination.limit,
        page: payload.pagination.page,
        sort: payload.pagination.sort,
        sort_type: payload.pagination.sort_type,
      },
    });

    return data;
  }
}

export const default_catalog_products_filters = (): Cart.CatalogProductRequestFilters => ({
  search: '',
  in_stock: false,
  categories: [],
  software_type: null,
  equipment_models: [],
  is_favorite_only: false,
  is_allowed_by_realization: false,
  warehouse_id: null,
});
